import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

const DeletedItemsModal = ({
  open,
  onClose,
  deletedItems = [],
  isDark,
}) => {

  const getItemDate = (item) => item.order?.date || item.created_at;

  const formatItemDate = (item) => {
    const itemDate = getItemDate(item);
    if (!itemDate) return '-';

    if (typeof itemDate === 'string' && itemDate.length >= 10) {
      return itemDate.slice(0, 10);
    }

    return new Date(itemDate).toLocaleDateString('ar-EG');
  };

  const formatItemTime = (item) => {
    if (!item.created_at) return '-';
    return new Date(item.created_at).toLocaleTimeString('ar-EG');
  };

  const getOrderTypeLabel = (type) => {
    switch (type) {
      case 'delivery':
        return 'ديلفيري';
      case 'tables':
        return 'طاولات';
      case 'takeaway':
        return 'تيك أواي';
      default:
        return type;
    }
  };

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'finished':
        return 'منتهى';
      case 'open':
        return 'مفتوح';
      case 'close':
        return 'مغلق';
      default:
        return status;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#232334' : '#fff',
          color: isDark ? '#fff' : '#232334',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: isDark ? '#29293a' : '#f5f5f5',
          fontWeight: 700,
          color: isDark ? '#f48fb1' : '#1976d2',
        }}
      >
        <span>تفاصيل الاصناف المحذوفة ({deletedItems.length})</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: isDark ? '#fff' : '#232334' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, bgcolor: isDark ? '#1a1a2e' : '#fafafa' }}>
        {deletedItems.length === 0 ? (
          <Typography sx={{ textAlign: 'center', py: 3, color: isDark ? '#aaa' : '#666' }}>
            لا توجد بيانات لعرضها
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? '#29293a' : '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    # رقم
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    اسم الصنف
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    السعر
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    رقم الطلب
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    نوع الطلب
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    حالة الطلب
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    المستخدم
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    التاريخ
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#f48fb1' : '#1976d2' }}>
                    الوصف
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedItems.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      bgcolor: isDark ? '#232334' : '#fff',
                      '&:hover': {
                        bgcolor: isDark ? '#29293a' : '#f0f4fa',
                      },
                      transition: 'background 0.2s',
                    }}
                  >
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334' }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334', fontWeight: 600 }}>
                      {item.product?.name || 'غير معروف'}
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334' }}>
                      {parseFloat(item.product?.price || 0).toFixed(2)} ج
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334' }}>
                      {item.order?.id || item.order_id || '-'}
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334' }}>
                      <Chip
                        label={getOrderTypeLabel(item.order?.order_type)}
                        size="small"
                        sx={{
                          bgcolor: item.order?.order_type === 'delivery' ? '#ff6b6b' :
                                  item.order?.order_type === 'tables' ? '#4c6ef5' :
                                  item.order?.order_type === 'takeaway' ? '#ffa94d' : '#868e96',
                          color: '#fff',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334' }}>
                      <Chip
                        label={getOrderStatusLabel(item.order?.status)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: item.order?.status === 'finished' ? '#51cf66' :
                                      item.order?.status === 'open' ? '#ffa94d' : '#ff6b6b',
                          color: item.order?.status === 'finished' ? '#51cf66' :
                                item.order?.status === 'open' ? '#ffa94d' : '#ff6b6b',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334' }}>
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <strong>{item.user?.name}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                          {item.user?.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334', fontSize: 12 }}>
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {formatItemDate(item)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                          {formatItemTime(item)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: isDark ? '#fff' : '#232334', maxWidth: 200 }}>
                      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                        {item.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: isDark ? '#29293a' : '#f5f5f5', p: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletedItemsModal;
